import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  SLIP_ACCEPT_ODD_CHANGES_SESSION_KEY,
  SLIP_MAX_MATCHES,
  SLIP_QUOTES_INTERVAL,
} from 'config/constants';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import { ToastMessagesIDs } from 'types/enums';
import {
  buildTicketEventsPayload,
  createSlip,
  fetchSlipQuotes,
} from 'services/api/slipService';
import { Bet } from 'types/Bet';
import { LiveEvent, LiveEventBet, LiveEventOdd } from 'types/LiveEvent';
import { MatchOffer } from 'types/MatchOffer';
import { Odd } from 'types/Odd';
import { SlipEntry } from 'types/SlipEntry';
import { UserInfo } from 'types/UserInfo';
import { LiveBettingContext } from './liveBettingContext';
import { UserContext } from './userContext';
import {
  applyServerOddsToEntries,
  hasLiveEnabledSelection,
  liveEntries,
  markKickedOffPicks,
} from 'helpers/liveSlipSubmit';
import { comboSum, validSystem } from 'helpers/systemBets';

export const SlipContext = React.createContext(null as any);

/** A new match past SLIP_MAX_MATCHES: a notice instead of the pick. */
const tellMaxMatches = () =>
  toast.warning(i18next.t('slip_max_matches', { count: SLIP_MAX_MATCHES }), {
    toastId: ToastMessagesIDs.SLIP_MAX_MATCHES,
  });

function readAcceptedOddChangesSession(): boolean {
  try {
    const raw = sessionStorage.getItem(SLIP_ACCEPT_ODD_CHANGES_SESSION_KEY);
    // Default ON: auto-accept BetsAPI price moves on submit (no confirm popup).
    // Uncheck to require the before/after confirm when server odds differ.
    if (raw === null) return true;
    const v = JSON.parse(raw) as unknown;
    return typeof v === 'boolean' ? v : true;
  } catch {
    return true;
  }
}

function sameId(a: unknown, b: unknown): boolean {
  return String(a) === String(b);
}

/** Live Full Time uses synthetic name "Full Time"; board markets keep provider NA. */
function liveEventHasOdd(
  liveEvent: LiveEvent,
  bet: LiveEventBet,
  odd: LiveEventOdd
): boolean {
  // Suspended by bet365 for the moment: on the slip it waits, greyed,
  // until the pick reopens (or the player removes it).
  const all = [
    ...(liveEvent.fullTimeBet?.odds ?? []),
    ...liveEvent.bets.flatMap((b) => b.odds ?? []),
  ];
  if (all.some((x) => sameId(x.custom_id, odd.custom_id) && x.suspended)) {
    return false;
  }
  if (
    liveEvent.fullTimeBet?.odds?.some((x) => sameId(x.custom_id, odd.custom_id))
  ) {
    return true;
  }
  const liveBet = liveEvent.bets.find(
    (x) =>
      x.name === bet.name ||
      x.name === bet.realName ||
      (bet.realName != null && x.name === bet.realName)
  );
  if (liveBet?.odds.some((x) => sameId(x.custom_id, odd.custom_id))) {
    return true;
  }
  // Fallback: odd id unique across the event (name mismatch after feed rename).
  return liveEvent.bets.some((b) =>
    b.odds.some((x) => sameId(x.custom_id, odd.custom_id))
  );
}

/** The entry as kept in storage: its match with only the picked markets. */
const storedEntry = (entry: SlipEntry): SlipEntry => {
  const match = entry.match as { bets?: { name?: string | null }[] };
  if (!Array.isArray(match.bets)) return entry;
  const picked = new Set(entry.bets.map((b) => b.bet?.name));
  return {
    ...entry,
    match: {
      ...entry.match,
      bets: match.bets.filter((b) => picked.has(b?.name ?? '')),
    } as SlipEntry['match'],
  };
};

const SlipProvider = ({ children }: any) => {
  const [entries, setEntries] = useState<SlipEntry[]>(
    JSON.parse(sessionStorage.getItem('slip') ?? '[]')
  );
  const [acceptedOddChanges, setAcceptedOddChangesState] = useState(
    readAcceptedOddChangesSession
  );
  const [maxCoefficient, setMaxCoefficient] = useState<number>(0);
  const [betNumber, setBetNumber] = useState(0);
  const [payoutCoefficient, setPayoutCoefficient] = useState(0);
  // System ticket: null plays the ordinary ticket (all matches at once);
  // otherwise the sizes played, [7] for "7 of 9" ([] while none is picked).
  const [system, setSystemState] = useState<number[] | null>(null);
  const setSystem = (next: number[] | null) =>
    setSystemState(
      next ? Array.from(new Set(next)).sort((a, b) => a - b) : null
    );

  const { events } = useContext(LiveBettingContext);
  const { loggedUser } = useContext(UserContext);

  // While the slip is open its live picks follow the provider's price, so the
  // player sees a move before paying. For showing only: the ticket's price
  // comes from the check after the click.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const watchPrices = !!loggedUser && hasLiveEnabledSelection(entries);
  useEffect(() => {
    if (!watchPrices) return;
    let stopped = false;
    const check = async () => {
      if (document.hidden) return;
      const payload = buildTicketEventsPayload(liveEntries(entriesRef.current));
      if (payload.length === 0) return;
      const legs = await fetchSlipQuotes(payload);
      if (stopped || legs.length === 0) return;
      setEntries((now) => applyServerOddsToEntries(now, legs) ?? now);
    };
    const timer = window.setInterval(check, SLIP_QUOTES_INTERVAL);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [watchPrices]);

  const setAcceptedOddChanges = (value: boolean) => {
    setAcceptedOddChangesState(value);
    try {
      sessionStorage.setItem(
        SLIP_ACCEPT_ODD_CHANGES_SESSION_KEY,
        JSON.stringify(value)
      );
    } catch {
      /* ignore quota / private mode */
    }
  };

  // Kept for a reload. Each match carried all its markets (about 200 kB for a
  // football match), so the 26th pick ran the browser's storage out and the
  // whole app fell to "System Error" (owner's video, 2026-11-03). Only the
  // picked markets are kept now, and a full storage never breaks the slip.
  useEffect(() => {
    try {
      sessionStorage.setItem('slip', JSON.stringify(entries.map(storedEntry)));
    } catch {
      /* ignore quota / private mode */
    }
  }, [entries]);

  // A system size the matches no longer allow (a match removed) is dropped.
  useEffect(() => {
    setSystemState((prev) => {
      if (!prev) return prev;
      const next = validSystem(prev, entries.length);
      return next.length === prev.length ? prev : next;
    });
  }, [entries.length]);

  useEffect(() => {
    if (entries.length === 0) {
      setMaxCoefficient(0);
      setBetNumber(0);
      setPayoutCoefficient(0);
      return;
    }
    // Combinations, as on forzza.com: one pick from every match each, so
    // the number of bets is the product of the picks per match and the
    // best a ticket can do is the best pick of every match on one bet. A
    // system plays every `size` of the matches the same way (comboSum).
    const counts: number[] = [];
    const best: number[] = [];
    let coeff = 1;
    let hasEnabled = false;
    entries.forEach((x) => {
      const enabled = x.bets.filter((b) => !b.disabled);
      let top = 0;
      enabled.forEach((b) => {
        const o = Number(b.odd.odds);
        if (Number.isFinite(o) && o > top) top = o;
      });
      if (top > 0) {
        coeff *= top;
        hasEnabled = true;
      }
      counts.push(Math.max(enabled.length, 1));
      best.push(top > 0 ? top : 1);
    });
    const sizes = system && system.length > 0 ? system : null;
    const n = Math.round(comboSum(counts, sizes));
    setBetNumber(n);
    // On a system the best odds of all its combinations together: the most
    // the ticket can win is the stake per combination x this.
    setMaxCoefficient(!hasEnabled ? 0 : sizes ? comboSum(best, sizes) : coeff);
    // What one unit of the total stake can win: the stake is split over
    // the bets, so the potential win is total stake x this.
    setPayoutCoefficient(hasEnabled && n > 0 ? comboSum(best, sizes) / n : 0);
  }, [entries, system]);

  useEffect(() => {
    setEntries((prev) => {
      const next = [...prev];
      let changed = false;

      const isLiveBetStillAvailable = (
        event: LiveEvent,
        bet: LiveEventBet,
        odd: LiveEventOdd
      ) => {
        const liveEvent = events
          .get(event.sportId)
          ?.find((x: LiveEvent) => sameId(x.fi, event.fi));
        if (!liveEvent) return false;
        return liveEventHasOdd(liveEvent, bet, odd);
      };

      next.forEach((entry) => {
        if (!entry.isLive) return;
        // Sports load one after another: a list not here yet says nothing.
        const sportId = (entry.match as LiveEvent).sportId;
        if (events.size > 0 && !events.has(sportId)) return;
        entry.bets.forEach((leg) => {
          const available = isLiveBetStillAvailable(
            entry.match as LiveEvent,
            leg.bet as LiveEventBet,
            leg.odd as LiveEventOdd
          );
          if (!available) {
            if (!leg.disabled) {
              leg.disabled = true;
              changed = true;
            }
            return;
          }
          // Odd still offered: clear false suspend only. Never overwrite slip prices —
          // board moves fast; locked pick prices are re-checked on BetsAPI at submit.
          if (leg.disabled) {
            leg.disabled = false;
            changed = true;
          }
        });
      });

      return changed ? next : prev;
    });
  }, [events]);

  // A prematch pick leaves the ticket when its match kicks off; it stays on
  // the slip, marked started, until the player removes it or takes it live.
  useEffect(() => {
    const mark = () => setEntries((prev) => markKickedOffPicks(prev));
    mark();
    const id = setInterval(mark, 15_000);
    return () => clearInterval(id);
  }, []);

  const addSlipEntry = (
    match: MatchOffer | LiveEvent,
    bet: Bet | LiveEventBet,
    odd: Odd | LiveEventOdd,
    isLive: boolean = false
  ) => {
    const setDisableTimeout = () => {
      const dateTime = new Date((match as MatchOffer).time * 1000);
      return setTimeout(
        () => enableDisableBetOdd(match, bet, odd, true),
        dateTime.getTime() - new Date().getTime()
      );
    };
    const entriesCopy = [...entries];
    let matchIndex = entriesCopy.findIndex((x) => sameId(x.match.fi, match.fi));
    // Freeze selected prices: clone odd so board refreshes cannot mutate slip values.
    const frozenOdd = { ...odd };
    const frozenBet = { ...bet };
    // Live and prematch share the FI, but the ticket reads live-or-not from the
    // entry: a live pick on a match whose prematch pick (now started) is on
    // the slip replaces that entry instead of joining it as prematch.
    if (matchIndex > -1 && entriesCopy[matchIndex].isLive !== isLive) {
      entriesCopy[matchIndex].bets.forEach((b) => clearTimeout(b.timeout));
      entriesCopy.splice(matchIndex, 1);
      matchIndex = -1;
    }
    if (matchIndex === -1 && entriesCopy.length >= SLIP_MAX_MATCHES) {
      tellMaxMatches();
      return;
    }
    if (matchIndex === -1) {
      entriesCopy.push({
        match,
        bets: [
          {
            bet: frozenBet,
            odd: frozenOdd,
            disabled: false,
            isLive: isLive,
            timeout: !isLive ? setDisableTimeout() : undefined,
          },
        ],
        isLive,
      });
    } else {
      // Another pick on the same match makes more combinations (see the
      // number of bets above).
      entriesCopy[matchIndex].bets.push({
        bet: frozenBet,
        odd: frozenOdd,
        disabled: false,
        isLive: isLive,
        timeout: !isLive ? setDisableTimeout() : undefined,
      });
    }
    setEntries([...entriesCopy]);
  };

  /**
   * Adds several prematch picks at once (the remaining matches of a ticket).
   * One state update, so none is lost to a stale `entries`; a pick already on
   * the slip is left as it is.
   */
  const addPrematchSlipEntries = (
    picks: { match: MatchOffer; bet: Bet; odd: Odd }[]
  ) => {
    setEntries((prev) => {
      const next = [...prev];
      let full = false;
      picks.forEach(({ match, bet, odd }) => {
        if (
          next.length >= SLIP_MAX_MATCHES &&
          !next.some((x) => sameId(x.match.fi, match.fi))
        ) {
          full = true;
          return;
        }
        const leg = {
          bet: { ...bet },
          odd: { ...odd },
          disabled: false,
          isLive: false,
          timeout: setTimeout(
            () => enableDisableBetOdd(match, bet, odd, true),
            match.time * 1000 - Date.now()
          ),
        };
        const matchIndex = next.findIndex((x) => sameId(x.match.fi, match.fi));
        if (matchIndex === -1) {
          next.push({ match, bets: [leg], isLive: false });
          return;
        }
        const entry = next[matchIndex];
        if (
          entry.isLive ||
          entry.bets.some((b) => sameId(b.odd.custom_id, odd.custom_id))
        ) {
          clearTimeout(leg.timeout);
          return;
        }
        next[matchIndex] = { ...entry, bets: [...entry.bets, leg] };
      });
      if (full) setTimeout(tellMaxMatches);
      return next;
    });
  };

  const removeSlipEntry = (
    match: MatchOffer | LiveEvent,
    bet: Bet | LiveEventBet,
    odd: Odd | LiveEventOdd
  ) => {
    const entriesCopy = [...entries];
    const matchIndex = entriesCopy.findIndex((x) => x.match.fi === match.fi);
    if (matchIndex > -1) {
      if (!bet && !odd) {
        entriesCopy[matchIndex].bets.forEach(
          (x) => x.timeout && clearTimeout(x.timeout)
        );
        entriesCopy.splice(matchIndex, 1);
      } else {
        if (entriesCopy[matchIndex].bets.length < 2) {
          entriesCopy[matchIndex].bets.forEach(
            (x) => x.timeout && clearTimeout(x.timeout)
          );
          entriesCopy.splice(matchIndex, 1);
        } else {
          const betIndex = entriesCopy[matchIndex].bets.findIndex(
            (x) =>
              sameId(x.bet.custom_id, bet.custom_id) &&
              sameId(x.odd.custom_id, odd.custom_id)
          );
          if (betIndex > -1) {
            if (entriesCopy[matchIndex].bets[betIndex].timeout) {
              clearTimeout(entriesCopy[matchIndex].bets[betIndex].timeout);
            }
            entriesCopy[matchIndex].bets.splice(betIndex, 1);
          }
        }
      }
      setEntries(entriesCopy);
    }
  };

  const entryExist = (
    match: MatchOffer | LiveEvent,
    bet: Bet | LiveEventBet,
    odd: Odd | LiveEventOdd
  ) => {
    let exists = false;
    const matchIndex = entries.findIndex((x) => x.match.fi === match.fi);
    if (matchIndex > -1) {
      if (
        entries[matchIndex].bets.findIndex(
          (x) =>
            sameId(x.bet.custom_id, bet.custom_id) &&
            sameId(x.odd.custom_id, odd.custom_id)
        ) > -1
      ) {
        exists = true;
      }
    }
    return exists;
  };

  const enableDisableBetOdd = (
    match: MatchOffer | LiveEvent,
    bet: Bet | LiveEventBet,
    odd: Odd | LiveEventOdd,
    disabled: boolean = true
  ) => {
    setEntries((prev: any) => {
      const entriesCopy = [...prev];
      const matchIndex = entriesCopy.findIndex(
        (x: any) => x.match.fi === match.fi
      );
      if (matchIndex > -1) {
        let betIndex = entriesCopy[matchIndex].bets.findIndex(
          (x: any) =>
            sameId(x.bet.custom_id, bet.custom_id) &&
            sameId(x.odd.custom_id, odd.custom_id)
        );
        if (betIndex > -1) {
          const entry = entriesCopy[matchIndex];
          const betEntry = entry.bets[betIndex];

          if (
            (!betEntry.isLive &&
              betEntry.disabled &&
              new Date().getTime() < (match as MatchOffer).time * 1000) ||
            (betEntry.isLive &&
              betEntry.disabled &&
              checkIfLiveBetExists(
                match as LiveEvent,
                bet as LiveEventBet,
                odd as LiveEventOdd
              ))
          ) {
            entriesCopy[matchIndex].bets[betIndex].disabled = disabled;
          }
        }
      }
      return entriesCopy;
    });
  };

  const removeAllEntries = () => {
    setEntries([]);
    setSystemState(null);
  };

  const submitSlip = async (totalStake: number, loggedUser: UserInfo) => {
    return await createSlip(
      entries,
      totalStake,
      loggedUser,
      system && system.length > 0 ? system : undefined
    );
  };

  const getTotalBets = () => {
    let total = 0;
    entries.forEach((x) => (total += x.bets.length));
    return total;
  };

  const checkIfLiveBetExists = (
    event: LiveEvent,
    bet: LiveEventBet,
    odd: LiveEventOdd
  ) => {
    const liveEvent = events
      .get(event.sportId)
      ?.find((x: LiveEvent) => sameId(x.fi, event.fi));
    if (!liveEvent) return false;
    return liveEventHasOdd(liveEvent, bet, odd);
  };

  return (
    <SlipContext.Provider
      value={{
        entries,
        maxCoefficient,
        payoutCoefficient,
        betNumber,
        system,
        setSystem,
        addSlipEntry,
        addPrematchSlipEntries,
        removeSlipEntry,
        entryExist,
        removeAllEntries,
        replaceSlipEntries: (next: SlipEntry[]) => {
          setEntries(next);
        },
        submitSlip,
        enableDisableBetOdd,
        getTotalBets,
        acceptedOddChanges,
        setAcceptedOddChanges,
      }}
    >
      {children}
    </SlipContext.Provider>
  );
};

export default SlipProvider;
